import React from 'react';
import { Box, Text } from '@chakra-ui/react';
import { Task } from '../api/taskApi';
import { TaskMentionRenderer } from '../utils/taskLinking';

interface TaskMentionDisplayProps {
  text: string;
  tasks: Task[];
  onTaskClick?: (taskId: string) => void;
  fontSize?: string;
  color?: string;
  lineHeight?: string | number;
  [key: string]: any; // Allow other Box props
}

/**
 * Component for displaying text with clickable task mentions
 * Used in task descriptions, comments, etc.
 */
export const TaskMentionDisplay: React.FC<TaskMentionDisplayProps> = ({
  text,
  tasks,
  onTaskClick,
  fontSize = "md",
  color = "gray.800",
  lineHeight = "1.6",
  ...boxProps
}) => {
  // If text contains HTML, we need to handle it differently
  const containsHTML = /<[^>]*>/.test(text);
  
  if (containsHTML) {
    // For HTML content, we'll need to parse and replace task mentions
    // This is more complex as we need to avoid replacing inside HTML tags
    return (
      <Box
        fontSize={fontSize}
        color={color}
        lineHeight={lineHeight}
        {...boxProps}
      >
        <div 
          dangerouslySetInnerHTML={{ 
            __html: replaceTaskMentionsInHTML(text, tasks, onTaskClick) 
          }} 
        />
      </Box>
    );
  }
  
  // For plain text, use the simple renderer
  return (
    <Box
      fontSize={fontSize}
      color={color}
      lineHeight={lineHeight}
      {...boxProps}
    >
      <TaskMentionRenderer
        text={text}
        tasks={tasks}
        onNavigate={onTaskClick}
      />
    </Box>
  );
};

/**
 * Replace task mentions in HTML content
 * This is more complex as we need to avoid replacing inside HTML tags
 */
function replaceTaskMentionsInHTML(
  html: string, 
  tasks: Task[], 
  onTaskClick?: (taskId: string) => void
): string {
  // Simple approach: replace task mentions in text nodes only
  // This is a basic implementation - for production, consider using a proper HTML parser
  
  const TASK_MENTION_PATTERN = /(?:#([A-Z]{2,}-[A-Z0-9-]+)|@task:([a-zA-Z0-9]+)|([A-Z]{2,}-[A-Z0-9-]+))/g;
  
  // Split by HTML tags to process only text content
  const parts = html.split(/(<[^>]*>)/);
  
  return parts.map((part, index) => {
    // If this part is an HTML tag, don't process it
    if (part.startsWith('<') && part.endsWith('>')) {
      return part;
    }
    
    // Process text content for task mentions
    return part.replace(TASK_MENTION_PATTERN, (match, hashTaskMatch, mongoIdMatch, plainTaskMatch) => {
      let taskId: string;
      let type: 'taskId' | 'mongoId' | 'projectNumber';
      
      if (hashTaskMatch) {
        taskId = hashTaskMatch;
        type = 'taskId';
      } else if (mongoIdMatch) {
        taskId = mongoIdMatch;
        type = 'mongoId';
      } else if (plainTaskMatch) {
        taskId = plainTaskMatch;
        type = 'projectNumber';
      } else {
        return match;
      }
      
      // Find the task
      const task = tasks.find(t => {
        switch (type) {
          case 'taskId':
          case 'projectNumber':
            return t.taskId === taskId;
          case 'mongoId':
            return t._id === taskId;
          default:
            return false;
        }
      });
      
      if (!task) {
        return `<span class="task-mention-not-found" style="color: #999; text-decoration: line-through;">${match}</span>`;
      }
      
      const clickHandler = onTaskClick ? `onclick="window.navigateToTask('${task._id}')"` : '';
      
      return `<span class="task-mention" style="background-color: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; cursor: pointer; text-decoration: none; font-weight: 500;" ${clickHandler} title="${task.taskTitle} (${task.status})">${match}</span>`;
    });
  }).join('');
}

// Global function for handling task navigation from HTML
declare global {
  interface Window {
    navigateToTask: (taskId: string) => void;
  }
}

/**
 * Initialize global task navigation handler
 * Call this once in your app initialization
 */
export const initializeTaskNavigation = (navigate: (path: string) => void) => {
  window.navigateToTask = (taskId: string) => {
    navigate(`/dashboard/task/${taskId}`);
  };
};
